/**
 * Mobile (below lg) view of the remove-cut confirmation: a full-height panel that slides in, with
 * Back / close, the question, and "Yes, Remove" / "Cancel". Chosen and wired by
 * `./RemoveCutModal.tsx`.
 *
 * The panel is always mounted. Opening and closing toggle its `slide-open` / `slide-close`
 * classes (styled by `components/Helper/commonStyles.tsx` → `SliderMenu`), and opening locks page
 * scrolling by overwriting the `<body>` `style`, which closing restores.
 */
import React, { createRef, RefObject, useEffect, useState } from "react";
import { useTheme } from "@ryerson/frontend.theme";
import { RemoveCutModalType } from "./RemoveCutModal";
import { Button } from "@ryerson/frontend.button";
import { Typography } from "@ryerson/frontend.typography";
import { ContentSection, Flex, FlexItem } from "@ryerson/frontend.layout";
import { useWindowSize } from "@ryerson/frontend.common";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import rgba from "polished/lib/color/rgba";
import { SliderMenu } from "@components/Helper/commonStyles";
import { Icon } from "@ryerson/frontend.assets";
import { useApplication } from "@ryerson/frontend.application";

const closeFiltersPane = (props: any) => {
	const { theme } = props;
	return css`
		height: 50px;
		width: 50px;
		border-left: 1px solid ${rgba(theme.colors.primary.white, 0.1)};
	`;
};

const GlobalNavigation = styled.div`
	height: 50px;
	width: 100%;
	top: 0;
	${(props: any) => {
		const { theme } = props;
		return css`
			border-bottom: 1px solid ${theme.colors.primary.lightGray};
			background-color: ${theme.colors.primary.darkGray};
		`;
	}}
`;

/**
 * Renders the mobile slide-in confirmation. On mount it calls `setShowModal(false)`, so the
 * panel always starts closed.
 */
const RemoveCutModalMobile: React.FC<RemoveCutModalType> = ({
	content,
	showModal,
	setShowModal = () => {},
	handleRemoveCut,
	handleClose,
}) => {
	const { theme } = useTheme();
	const { gray, text, background } = theme.colors.primary;
	const { isNative } = useApplication();

	const [domNodeRefs] = useState<any>({});
	const [hasMounted, setHasMounted] = React.useState<boolean>(false);
	const [bodyStyles, setBodyStyles] = useState<string>("");
	const windowSize = useWindowSize();

	const creatDomNodeRef = (fieldName: string): RefObject<any> => {
		domNodeRefs[fieldName] = createRef();
		return domNodeRefs[fieldName];
	};

	// Manage slider animation and body scrolling
	// The classes are toggled, not set from `showModal`, so the panel's state relies on every
	// change of `showModal` after mount being a real open/close flip.
	useEffect(() => {
		if (hasMounted) {
			const sliderEl = domNodeRefs["remove-define-cut"].current;

			sliderEl?.classList?.toggle("slide-open");
			sliderEl?.classList?.toggle("slide-close");

			const body = document?.querySelector("body");

			if (showModal) {
				(window as any).scrollTo(0, 0);
				setBodyStyles(body?.getAttribute("style") || "");
				setTimeout(() => {
					body?.setAttribute("style", "overflow: hidden;");
				}, 100);
			} else {
				body && body.setAttribute("style", bodyStyles);
			}
		}
	}, [showModal]);

	useEffect(() => {
		setHasMounted(true);
		setShowModal(false);

		const body = document?.querySelector("body");

		if (showModal) {
			(window as any).scrollTo(0, 0);
			setBodyStyles(body?.getAttribute("style") || "");
			setTimeout(() => {
				body?.setAttribute("style", "overflow: hidden;");
			}, 100);
		} else {
			body && body.setAttribute("style", bodyStyles);
		}
	}, []);

	return (
		<>
			<SliderMenu
				{...{ theme: theme }}
				ref={creatDomNodeRef(`remove-define-cut`)}
				className="remove-define-cut slide-close"
				css={{ top: isNative ? "calc(env(safe-area-inset-top) + 60px)" : 100 }}
			>
				<Flex css={{ height: windowSize[1] - 100, overflowY: "scroll" }} direction="column">
					<GlobalNavigation theme={theme}>
						<Flex alignItems="center">
							<FlexItem grow={1}>
								<Flex justifyContent="flex-start" onClick={handleClose}>
									<FlexItem alignSelf="center" css={{ marginLeft: 20 }}>
										<Icon
											icon="chevron-left"
											size="md"
											onClick={handleClose}
											color={background}
											circled={true}
											css={{ borderColor: gray }}
										/>
									</FlexItem>
									<FlexItem
										css={css`
											padding-left: 10px;
										`}
									>
										<Typography
											variant="p"
											color={theme.colors.tertiary.label}
											size="md"
										>
											{content?.back}
										</Typography>
									</FlexItem>
								</Flex>
							</FlexItem>
							<FlexItem>
								<Flex
									style={closeFiltersPane({ theme: theme })}
									justifyContent="center"
									alignItems="center"
								>
									<Icon
										icon="close"
										size="sm"
										onClick={handleClose}
										color={theme.colors.primary.white}
									/>
								</Flex>
							</FlexItem>
						</Flex>
					</GlobalNavigation>
					<ContentSection vPadding="0" hPadding="0" type="secondary">
						<Flex
							alignItems="flex-start"
							css={{ padding: "27px 22px 13px", width: "100%", gap: 14 }}
							direction="column"
						>
							<Typography type="tertiary" color={text}>
								{content.areYouSure}
							</Typography>
						</Flex>
						<Flex
							justifyContent="space-between"
							alignItems="center"
							css={{ width: "100%", padding: "10px 22px" }}
							direction="column"
						>
							<Button
								label={content.yesRemove}
								size="lg"
								onClick={handleRemoveCut}
								fullwidth={true}
								css={{ width: "100%", padding: "10px 0" }}
								dataTestId="remove-cut-modal-mobile-remove-button"
							/>
							<Button
								label={content.cancel}
								size="lg"
								onClick={handleClose}
								paddingType="relaxed"
								foreground="light"
								background={gray}
								fullwidth={true}
								css={{ width: "100%", padding: "10px 0" }}
								dataTestId="remove-cut-modal-mobile-cancel-button"
							/>
						</Flex>
					</ContentSection>
				</Flex>
			</SliderMenu>
		</>
	);
};

export default RemoveCutModalMobile;
